/**
 * /recuperar — request a password-reset email.
 *
 * Deliberately does NOT reveal whether the address has an account: the success
 * panel is identical either way (account enumeration would leak which municipal
 * addresses are provisioned). The branded PT email itself is configured
 * server-side by `tools/supabase/setup.ts` (mailer_subjects_recovery +
 * templates/recovery.html).
 */
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuth } from '../contexts/AuthProvider';
import { translateAuthError } from '../lib/authErrors';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const EMBER_BTN =
  'w-full inline-flex items-center justify-center gap-2 rounded-xl bg-ember px-4 py-3 font-semibold text-ink-950 shadow-[0_0_24px_-10px_#F26A4B] transition-all duration-200 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed';
const ERR_BOX =
  'rounded-xl bg-risk-high/10 border border-risk-high/30 text-risk-high text-sm p-3';
const OK_BOX =
  'rounded-xl bg-tutela-growth/10 border border-tutela-growth/30 text-tutela-growth text-sm p-3';

export default function Recuperar() {
  useDocumentMeta('Recuperar palavra-passe | Tutela', undefined, undefined, { noindex: true });
  const { requestPasswordReset, configured } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await requestPasswordReset(email.trim());
    setLoading(false);
    // Rate-limit / malformed-address errors are worth showing; "user not found"
    // is not returned by Supabase here, so a success panel is safe.
    if (error) {
      setError(translateAuthError(error));
      return;
    }
    setSent(true);
  }

  return (
    <AuthLayout
      title="Repor a palavra-passe"
      subtitle={
        sent
          ? undefined
          : 'Indique o seu email. Se existir uma conta, enviamos uma ligação para definir uma nova palavra-passe.'
      }
      footer={
        <>
          Lembrou-se?{' '}
          <Link to="/entrar" className="text-ember hover:underline">
            Entrar
          </Link>
        </>
      }
    >
      {!configured && (
        <p className={`mb-4 ${ERR_BOX}`}>Autenticação indisponível nesta configuração.</p>
      )}

      {sent ? (
        <div className="space-y-4">
          <p className={OK_BOX}>
            Se existir uma conta associada a <strong>{email.trim()}</strong>, receberá um email com
            uma ligação para definir uma nova palavra-passe.
          </p>
          <p className="text-sm text-mist-muted">
            A ligação é válida por tempo limitado e só pode ser usada uma vez. Verifique também a
            pasta de spam. <strong>Abra a ligação no mesmo dispositivo e navegador</strong> onde fez
            este pedido.
          </p>
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setError(null);
            }}
            className="text-sm text-mist-muted hover:text-mist transition-colors"
          >
            Enviar novamente
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label-dark" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              className="input-dark"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@organizacao.pt"
            />
          </div>
          {error && <p className={ERR_BOX}>{error}</p>}
          <button type="submit" disabled={loading || !configured} className={EMBER_BTN}>
            {loading ? 'A enviar...' : 'Enviar ligação'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
